/**
 * 「VoIPオプション変更」に関連するロジック用ファイルです。
 */
import { VoipOptionChangeStateType } from '@/states/voipOptionChangeState';
import { PutVoipOptionChangeRequest } from '@/types/api/mypage/voipoptioninfo';
import { VoipOptionPriceResponse } from '@/types/api/mypage/voipoptionprice';
import {
  containCatchPhone,
  containNumberDisplay,
  containNumberRequest,
  containStopNuisanceCall,
  containVoiceWarp,
} from '@/utils/common';
import { Constants } from '@/utils/constants';
import { VoipOptionUseStatus } from '@/utils/constants/voip';
import { getVoipOption } from '@/logics/option/voip/voip';

/**
 * 「VoIP付加サービス変更」の「単品オプション」に設定する値を作成する。
 * 現在契約状態と画面上の選択状態の意味が逆転するため、API 送信用の真偽値へ正規化する。
 * @param input 入力値
 * @param includeServicePack サービスパック含むかどうか
 * @param useStatus 現在の契約ステータス
 * @returns 設定値
 */
export const createPutVoipOptionValue = (
  input: boolean,
  includeServicePack: boolean,
  useStatus: string | undefined
): boolean => {
  // 対象オプションが「サービスパック」に含まれている場合は、常に同じ値を返却する。
  if (includeServicePack) {
    return false;
  }
  if (useStatus === VoipOptionUseStatus.SERVICE) {
    // 「利用中」の場合は「選択 = 解約する」、「未選択 = 利用する」の状態にする。
    if (input) {
      return false;
    }
    return true;
  }
  // 「利用中以外」の場合は「選択 = 利用する」、「未選択 = 解約する」の状態にする。
  if (input) {
    return true;
  }
  return false;
};

/**
 * 「VoIP付加サービス変更」のリクエスト情報を作成する。
 * 画面 state と現在契約情報を突き合わせ、API が受け付ける更新形式へ詰め替える。
 * @param changeVoipOptionInfo 入力情報
 * @param voipOptionPrice VoIPオプション利用状況取得のレスポンス
 * @param runType 実行区分
 * @returns リクエスト情報
 */
export const createPutVoipOptionChangeRequest = (
  changeVoipOptionInfo: VoipOptionChangeStateType,
  voipOptionPrice: VoipOptionPriceResponse,
  runType: string
): PutVoipOptionChangeRequest => {
  // 「発信者番号通知サービス」の申込情報を取得する。
  const numberNotice = createPutVoipOptionValue(
    changeVoipOptionInfo.optionNumberNotice,
    false,
    getVoipOption(Constants.VoipOptionClassType.VOIP_SENDER_NUMBER_INFO, voipOptionPrice)
      ?.use_status
  );

  // 「番号表示サービス」の申込情報を取得する。
  const numberDisplay = createPutVoipOptionValue(
    changeVoipOptionInfo.optionNumberDisplay,
    containNumberDisplay(changeVoipOptionInfo.optionServicePack),
    getVoipOption(Constants.VoipOptionClassType.VOIP_NUMBER_DISPLAY, voipOptionPrice)?.use_status
  );

  // 「番号通知リクエストサービス」の申込情報を取得する。
  const numberRequest = createPutVoipOptionValue(
    changeVoipOptionInfo.optionNumberRequest,
    containNumberRequest(changeVoipOptionInfo.optionServicePack),
    getVoipOption(Constants.VoipOptionClassType.VOIP_NUMBER_INFO, voipOptionPrice)?.use_status
  );

  // 「キャッチ電話サービス」の申込情報を取得する。
  const catchPhone = createPutVoipOptionValue(
    changeVoipOptionInfo.optionCatchPhone,
    containCatchPhone(changeVoipOptionInfo.optionServicePack),
    getVoipOption(Constants.VoipOptionClassType.VOIP_CATCH, voipOptionPrice)?.use_status
  );

  // 「着信お断りサービス」の申込情報を取得する。
  const voiceWarp = createPutVoipOptionValue(
    changeVoipOptionInfo.optionVoiceWarp,
    containVoiceWarp(changeVoipOptionInfo.optionServicePack),
    getVoipOption(Constants.VoipOptionClassType.VOIP_TRANSFER, voipOptionPrice)?.use_status
  );

  // 「着信転送サービス」の申込情報を取得する。
  const stopNuisanceCall = createPutVoipOptionValue(
    changeVoipOptionInfo.optionStopNuisanceCall,
    containStopNuisanceCall(changeVoipOptionInfo.optionServicePack),
    getVoipOption(Constants.VoipOptionClassType.VOIP_BLOCK_CALL, voipOptionPrice)?.use_status
  );

  // API 送信時は画面上の項目名ではなく、更新用フィールド名へ明示的に詰め替える。
  return {
    option_number_notice: numberNotice,
    option_number_display: numberDisplay,
    option_number_request: numberRequest,
    option_catch_phone: catchPhone,
    option_voice_warp: voiceWarp,
    option_stop_nuisance_call: stopNuisanceCall,
    option_service_pack: changeVoipOptionInfo.optionServicePack,
    request_info: {
      run_type: runType,
    },
  };
};
